import type { ReactNode } from 'react';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import '@fontsource-variable/inter';
import './cutting-theme.css';

/**
 * Block 14 «Раскрой» — PR #2-UI. Лейаут-шелл авторской темы (ADR-0061).
 *
 * Структура прототипа: шапка `.sc-header` (оранжевая, без логотипа, §IX.X #4) →
 * опциональный TabBar → прокручиваемый контент → глобальный `<BottomBar>`
 * (P-023/P-036 — реальная навигация, вместо alert-заглушки прототипа).
 *
 * Scroll живёт на inner `.overflow-y-auto` (P-046/P-047). Inter подключается
 * self-hosted и только при загрузке темы Раскроя (не в глобальный main.tsx).
 */
export function CuttingLayout({
  title,
  autosave = false,
  autosaveLabel,
  tabBar,
  mobileHeaderless = false,
  children,
}: {
  title: string;
  autosave?: boolean;
  /** Живой текст индикатора автосейва (CUT-1.6). Если задан — рендерится вместо статичного. */
  autosaveLabel?: string;
  tabBar?: ReactNode;
  /**
   * PR #8a (ADR-0069 D1): на мобильном (≤480px) скрыть оранжевую `.sc-header` для
   * экономии вертикали в цеху. Передают не-Старт экраны (Project/History); Старт —
   * нет (сохраняет шапку с названием «Раскрой»). Скрытие — CSS media-only (см.
   * `.cutting-mobile-headerless` в cutting-theme.css), без JS-unmount: десктоп/планшет
   * и тестовые локаторы (e2e Desktop Chrome 1280px, jsdom) не затронуты.
   */
  mobileHeaderless?: boolean;
  children: ReactNode;
}) {
  const showAutosave = autosaveLabel !== undefined || autosave;
  return (
    <main
      className={`cutting-theme min-h-screen flex flex-col${
        mobileHeaderless ? ' cutting-mobile-headerless' : ''
      }`}
    >
      <header className="sc-header">
        <h1 className="sc-screen-title">{title}</h1>
        {showAutosave ? (
          <span className="sc-autosave" aria-live="polite">
            {autosaveLabel ?? '💾 сохр.'}
          </span>
        ) : null}
      </header>
      {tabBar}
      <section className="flex-1 overflow-y-auto">{children}</section>
      <BottomBar variant="default" />
    </main>
  );
}
